// Shared shapes for the two PTO surfaces: /pto (mine) and /admin/pto (everyone's).

export type PtoStatus = 'pending' | 'approved' | 'denied' | 'cancelled';

export interface PtoRequest {
  id: string;
  agentId: string;
  agentName: string;
  startDate: string;
  endDate: string;
  firstDayHalf: boolean;
  lastDayHalf: boolean;
  days: number;
  status: PtoStatus;
  reason: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
}

export interface PtoBalance {
  eligible: boolean;
  ineligibleReason: 'part_time' | 'no_hire_date' | null;
  hiredOn: string | null;
  yearStart: string | null;
  yearEnd: string | null;
  yearIndex: number | null;
  accruedDays: number;
  usedDays: number;
  pendingDays: number;
  adjustmentDays: number;
  availableDays: number;
  nextAccrualOn: string | null;
  maxDaysPerRun: number;
  asOf: string;
}

export interface PtoAdjustment {
  id: string;
  agentId: string;
  effectiveOn: string;
  days: number;
  note: string;
  createdByName: string | null;
  createdAt: string;
}

export interface MyPtoResponse {
  agentId: string;
  balance: PtoBalance;
  requests: PtoRequest[];
  adjustments: PtoAdjustment[];
}

export interface PtoPreview {
  days: number;
  dates: { date: string; fraction: number }[];
  problem: { code: string; message: string } | null;
}

export interface PtoRosterRow {
  agentId: string;
  name: string;
  email: string;
  isFullTime: boolean;
  hiredOn: string | null;
  balance: PtoBalance;
}

// Days render as "3" or "2.5", never "3.0" — a half day is the only fraction
// that exists, so trailing zeros are noise.
export function fmtDays(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

export function dayLabel(n: number): string {
  return `${fmtDays(n)} ${n === 1 ? 'day' : 'days'}`;
}

// YYYY-MM-DD -> "Mon 24 Aug 2026", parsed as a local calendar date rather
// than a UTC instant (new Date('2026-08-24') is the evening before, here).
export function fmtDate(iso: string, opts?: Intl.DateTimeFormatOptions): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y!, (m ?? 1) - 1, d ?? 1);
  return date.toLocaleDateString(
    undefined,
    opts ?? { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' },
  );
}

export function fmtRange(r: { startDate: string; endDate: string }): string {
  const short: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  if (r.startDate === r.endDate) return fmtDate(r.startDate);
  const sameYear = r.startDate.slice(0, 4) === r.endDate.slice(0, 4);
  return `${fmtDate(r.startDate, short)} – ${fmtDate(r.endDate, sameYear ? { ...short, year: 'numeric' } : undefined)}`;
}

export const STATUS_CLASSES: Record<PtoStatus, string> = {
  pending: 'border-amber-300 bg-amber-50 text-amber-800',
  approved: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  denied: 'border-rose-200 bg-rose-50 text-rose-800',
  cancelled: 'border-[var(--color-border)] bg-white text-[var(--color-muted)]',
};

export function todayIso(): string {
  const now = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
